import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EmailMessageModel,
  useEmailMessages,
  emailMessages$,
} from "@jugl-web/rest-api";
import { LoadingAnimation, DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { VList, VListHandle } from "virtua";
import { useDebounce, isoToLocalDate, useTranslations } from "@jugl-web/utils";
import { format, isThisYear, isToday } from "date-fns";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { ReactComponent as ChevronsDownIcon } from "../WhatsappChatMessageWindow/assets/chevrons-down.svg";
import { EmailMessageItem } from "../EmailMessageItem";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";

export interface EmailChatMessageWindowHandle {
  scrollToBottom: () => void;
  addMessage: (
    message: EmailMessageModel,
    forceScrollToBottom?: boolean
  ) => void;
}

type EmailChatMessageItem =
  | { type: "message"; value: EmailMessageModel }
  | { type: "date"; value: string };

export const EmailChatMessageWindow = forwardRef<
  EmailChatMessageWindowHandle,
  { conversationId: string }
>(({ conversationId }, ref) => {
  const { entityId } = useEntitySelectedProvider();
  const { incomingEmailMessages$ } = useClientSupportPageContext();
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const { dateLocale } = useLanguage();
  const { t } = useTranslations();
  const prevLenRef = useRef(0);
  const prevScrollOffsetRef = useRef(0);
  const wasLoadingMore = useRef(false);

  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);

  const {
    data: loadedMessages,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reset,
  } = useEmailMessages({
    entityId,
    conversationId,
    pageSize: 50,
  });

  const emailMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: EmailChatMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.payload.timestamp);
      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  // Reset when conversation changes
  useEffect(() => {
    setIsInitialScrollInitialized(false);
    reset();
  }, [conversationId, reset]);

  const scrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(emailMessageItems.length, {
      align: "end",
    });
    setAtBottom(true);
  }, [emailMessageItems.length]);

  // Initial scroll to bottom
  useEffect(() => {
    if (
      !isInitialScrollInitialized &&
      !isInitialScrollInitializationInProgress.current &&
      emailMessageItems.length > 0 &&
      !isLoading
    ) {
      isInitialScrollInitializationInProgress.current = true;
      setTimeout(() => {
        vListRef.current?.scrollToIndex(emailMessageItems.length - 1, {
          align: "end",
        });
        setIsInitialScrollInitialized(true);
        isInitialScrollInitializationInProgress.current = false;
      }, 100);
    }
  }, [
    emailMessageItems.length,
    isLoading,
    isInitialScrollInitialized,
    scrollToBottom,
  ]);

  const addMessage = useCallback(
    (message: EmailMessageModel, forceScrollToBottom?: boolean) => {
      if (forceScrollToBottom || atBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    },
    [atBottom, scrollToBottom]
  );

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    addMessage,
  }));

  useEffect(() => {
    const subscription = incomingEmailMessages$.subscribe((message) => {
      if (message.conv_id !== conversationId) return;

      emailMessages$.next({ type: "append", message });

      if (atBottom) {
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [incomingEmailMessages$, conversationId, atBottom, scrollToBottom]);

  const handleLoadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      prevLenRef.current = emailMessageItems.length;
      prevScrollOffsetRef.current = vListRef.current?.scrollOffset ?? 0;
      wasLoadingMore.current = true;
      await loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, emailMessageItems.length]);

  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMore.current = true;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (
      wasLoadingMore.current &&
      !isLoadingMore &&
      emailMessageItems.length > prevLenRef.current
    ) {
      vListRef.current?.scrollToIndex(
        emailMessageItems.length - prevLenRef.current,
        {
          align: "start",
          offset: prevScrollOffsetRef.current,
        }
      );
      wasLoadingMore.current = false;
    }
    prevLenRef.current = emailMessageItems.length;
  }, [emailMessageItems, isLoadingMore]);

  return (
    <div className="relative flex h-full flex-col bg-gray-100">
      <DataLoadingWrapper
        isError={isError}
        isFetching={false}
        isLoading={isLoading && loadedMessages.length === 0}
        onRetry={reset}
      >
        <VList
          ref={vListRef}
          reverse
          className="flex-1 pb-6"
          onScroll={(offset) => {
            if (
              !vListRef.current ||
              isLoadingMore ||
              !isInitialScrollInitialized
            ) {
              return;
            }
            const bottomOffset =
              offset -
              vListRef.current.scrollSize +
              vListRef.current.viewportSize;

            setAtBottom(bottomOffset > -5);
            // Load more messages when scrolling up
            if (offset < 150 && hasMore) {
              handleLoadMore();
            }
          }}
        >
          {isLoading && loadedMessages.length > 0 && hasMore && (
            <div className="flex justify-center py-4">
              <LoadingAnimation size="sm" />
            </div>
          )}
          {emailMessageItems.map((item, index) => (
            <div
              key={item.type === "date" ? `date-${+index}` : item.value.id}
              className="mx-auto w-full max-w-[1000px]"
            >
              {item.type === "date" ? (
                <ChatDateHeader date={item.value} />
              ) : (
                <EmailMessageItem message={item.value} />
              )}
            </div>
          ))}
        </VList>
      </DataLoadingWrapper>

      {!debouncedAtBottom && (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className="absolute right-4 bottom-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary-700 shadow-lg transition-opacity hover:opacity-90"
        >
          <ChevronsDownIcon className="text-white" />
        </button>
      )}
    </div>
  );
});

EmailChatMessageWindow.displayName = "EmailChatMessageWindow";
